import { useEffect, useState } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { findAgencyContacts, saveFoundContact } from '@generated/server/findContacts';
import { getStaffPass } from '@generated/hooks/staffPass';
import { guessEmails } from '@generated/hooks/emailGuess';

type Result = Awaited<ReturnType<typeof findAgencyContacts>>;
type Contact = Result['contacts'][number];

export function FindContacts({ agency, onClose, onSaved }: { agency: { id: string; name: string } | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const [res, setRes] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState('');
  const [done, setDone] = useState<Record<string, string>>({});
  const [emails, setEmails] = useState<Record<string, string>>({});
  const fallbackDomain = res?.generalEmail.split('@')[1] ?? '';
  useEffect(() => {
    if (!res) return;
    setEmails(Object.fromEntries(res.contacts.map((c, i) => [String(i), c.email || guessEmails(c.name, res.emailPattern, res.generalEmail.split('@')[1] ?? '')[0] || ''])));
  }, [res]);
  /** The contact as it will be saved: with the (possibly edited / pattern-built) email. */
  const withEmail = (c: Contact, key: string): Contact => { const e = (emails[key] ?? c.email).trim(); return { ...c, email: e, emailKind: e && e.toLowerCase() === c.email.toLowerCase() ? c.emailKind : e ? 'pattern guess' : 'none' }; };

  useEffect(() => {
    if (!agency) return;
    let on = true;
    setRes(null); setErr(''); setDone({}); setLoading(true);
    findAgencyContacts({ data: { pass: getStaffPass(), id: agency.id } })
      .then((r) => on && setRes(r ?? null))
      .catch((e) => { console.error(e); if (on) setErr(e instanceof Error && e.message.startsWith('CREDITS:') ? e.message.slice(9) : 'The search did not work this time. Please try again in a minute.'); })
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, [agency]);

  async function save(c: Contact, mode: 'main' | 'notes' | 'list', key: string) {
    if (!agency) return;
    setSaving(key + mode);
    try {
      await saveFoundContact({ data: { pass: getStaffPass(), id: agency.id, mode, ...c } });
      if (key === 'general') { setDone((d) => ({ ...d, general: 'Added to the email list' })); onSaved(`General inbox added for ${agency.name}.`); return; }
      setDone((d) => ({ ...d, [key]: mode === 'main' ? 'Saved as main contact' : mode === 'list' ? 'Added to the email list' : 'Added to notes' }));
      onSaved(`${c.name} saved to ${agency.name}.`);
    } catch (e) { console.error(e); setErr('Could not save that contact.'); } finally { setSaving(''); }
  }
  async function addAll() {
    if (!agency || !res) return;
    setSaving('all');
    let n = 0;
    try {
      for (const [i, c0] of res.contacts.entries()) {
        const c = withEmail(c0, String(i));
        if (!c.email.includes('@') || done[String(i)]) continue;
        await saveFoundContact({ data: { pass: getStaffPass(), id: agency.id, mode: 'list', ...c } });
        setDone((d) => ({ ...d, [String(i)]: 'Added to the email list' })); n++;
      }
      onSaved(`Added ${n} ${n === 1 ? 'person' : 'people'} at ${agency.name} to the email list.`);
    } catch (e) { console.error(e); setErr('Could not add everyone. Please try again.'); } finally { setSaving(''); }
  }
  const b = 'inline-flex min-h-10 items-center gap-1 border-2 border-foreground px-3 text-xs font-semibold disabled:opacity-50';

  return (
    <Dialog open={!!agency} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-2 border-foreground sm:max-w-2xl">
        <DialogHeader><DialogTitle>Find contacts at {agency?.name}</DialogTitle></DialogHeader>
        {loading && <p className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Searching the web for media buyers and account people… this takes about 20–40 seconds.</p>}
        {err && <p className="text-sm text-destructive">{err}</p>}
        {res && (
          <div className="space-y-3">
            {res.summary && <p className="border-l-4 border-primary bg-muted px-3 py-2 text-sm">{res.summary}</p>}
            <p className="text-xs text-muted-foreground">
              Everyone on the email list gets each letter (their own first name in the greeting). Email pattern: <b>{res.emailPattern || 'not found'}</b>{res.generalEmail && <> · General email: <b>{res.generalEmail}</b></>}
            </p>
            {res.contacts.some((c, i) => withEmail(c, String(i)).email.includes('@')) && (
              <button disabled={!!saving} onClick={addAll} className={`${b} min-h-11 bg-primary text-sm text-primary-foreground`}>{saving === 'all' && <Loader2 className="h-3 w-3 animate-spin" />}Add everyone with an email to the email list</button>
            )}
            {res.generalEmail.includes('@') && (
              <div className="flex flex-wrap items-center gap-2 border-2 border-dashed border-foreground p-3 text-sm">
                <span className="min-w-0 flex-1 break-words">General inbox: <b>{res.generalEmail}</b></span>
                {done.general ? <span className="text-xs font-semibold">{done.general}</span> : <button disabled={!!saving} onClick={() => save({ name: '', title: 'General inbox', email: res.generalEmail, emailKind: 'published', source: '' }, 'list', 'general')} className={`${b} bg-card`}>{saving === 'generallist' && <Loader2 className="h-3 w-3 animate-spin" />}Add to email list</button>}
              </div>
            )}
            {res.contacts.length === 0 && <p className="text-sm">No named people found. Try LinkedIn → the agency's page → People, and search "media".</p>}
            <ul className="divide-y-2 divide-foreground/15 border-2 border-foreground">
              {res.contacts.map((c, i) => {
                const key = String(i);
                return (
                  <li key={key} className="space-y-2 p-3">
                    <div>
                      <p className="font-bold">{c.name} <span className="font-normal text-muted-foreground">· {c.title}</span></p>
                      {(() => {
                        const cur = withEmail(c, key);
                        const options = guessEmails(c.name, res.emailPattern, fallbackDomain).filter((o) => o !== cur.email.toLowerCase());
                        return (
                          <div className="mt-1 space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <input value={emails[key] ?? ''} onChange={(e) => setEmails((m) => ({ ...m, [key]: e.target.value }))} placeholder="No email found – type one" aria-label={`Email for ${c.name}`} className="min-h-10 min-w-0 flex-1 border-2 border-foreground bg-background px-2 text-sm" />
                              {cur.email && <span className={`border px-1.5 text-[10px] font-semibold uppercase ${cur.emailKind === 'published' ? 'border-foreground' : 'border-destructive text-destructive'}`}>{cur.emailKind === 'published' ? 'found online' : 'guessed – check first'}</span>}
                            </div>
                            {options.length > 0 && <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">Other likely:{options.map((o) => <button key={o} onClick={() => setEmails((m) => ({ ...m, [key]: o }))} className="border border-foreground/40 px-1.5 py-0.5 hover:bg-muted">{o}</button>)}</p>}
                          </div>
                        );
                      })()}
                      {c.source && <a href={c.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-xs underline">{c.source}<ExternalLink className="h-3 w-3 shrink-0" /></a>}
                    </div>
                    {done[key] ? <p className="text-xs font-semibold">{done[key]}</p> : (
                      <div className="flex flex-wrap gap-2">
                        {withEmail(c, key).email.includes('@') && <button disabled={!!saving} onClick={() => save(withEmail(c, key), 'list', key)} className={`${b} bg-primary text-primary-foreground`}>{saving === key + 'list' && <Loader2 className="h-3 w-3 animate-spin" />}Add to email list</button>}
                        <button disabled={!!saving} onClick={() => save(withEmail(c, key), 'main', key)} className={`${b} bg-card`}>{saving === key + 'main' && <Loader2 className="h-3 w-3 animate-spin" />}Use as main contact</button>
                        <button disabled={!!saving} onClick={() => save(withEmail(c, key), 'notes', key)} className={`${b} bg-card`}>{saving === key + 'notes' && <Loader2 className="h-3 w-3 animate-spin" />}Add to notes</button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
